import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Input, Select, Toast, Popconfirm, Form } from '@douyinfe/semi-ui';
import { IconSearch, IconEdit, IconDelete, IconPlus, IconStop, IconPlayCircle } from '@douyinfe/semi-icons';
import request from '../../utils/request';
import styles from './index.module.scss';
import ImageSelector from '../../components/ImageSelector';
import UniImage from '../../components/UniImage';
import LinkSelector from '../../components/LinkSelector';
import { useSearchParams } from 'react-router-dom';

const Slides = () => {
    const [searchParams] = useSearchParams();
    const urlTyp = searchParams.get('typ');

    const [formApi, setFormApi] = useState(null);
    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [pagination, setPagination] = useState({ currentPage: 1, pageSize: 10, total: 0 });
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [currentRecord, setCurrentRecord] = useState({});
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [searchKey, setSearchKey] = useState('');
    const [searchStatus, setSearchStatus] = useState(null);
    const [searchTyp, setSearchTyp] = useState(urlTyp ? Number(urlTyp) : null);
    const [typList, setTypList] = useState([]);

    useEffect(() => {
        const fetchTypList = async () => {
            try {
                const response = await request.get('/go/sp/all/list_all_slide_typ/');
                if (response.errno === 0) {
                    setTypList(response.list || []);
                } else {
                    Toast.error(response.errstr || '获取分类失败');
                }
            } catch (error) {
                console.error('获取分类失败:', error);
                Toast.error('获取分类失败');
            }
        };
        fetchTypList();
    }, []);

    const statusMap = {
        0: { text: '正常', type: 'success' },
        1: { text: '隐藏', type: 'warning' }
    };

    const fetchList = async (page = 1, limit = 10) => {
        setLoading(true);
        try {
            const response = await request.get('/go/sp/all/list_all_slide/', {
                params: {
                    page: page - 1,
                    limit,
                    key: searchKey,
                    status: searchStatus === null ? -1 : searchStatus,
                    typ: searchTyp === null ? -1 : searchTyp
                }
            });
            if (response.errno === 0) {
                setDataSource(response.list || []);
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    total: response.total_cnt
                }));
            } else {
                Toast.error(response.errstr || '获取列表失败');
            }
        } catch (error) {
            console.error('获取列表失败:', error);
            Toast.error('获取列表失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (typList.length > 0) {
            fetchList();
        }
    }, [typList]);

    const handleAdd = async (values) => {
        if (!values.image) {
            Toast.error('请选择图片');
            return;
        }
        try {
            const response = await request.post('/go/sp/all/add_all_slide/', {
                ...values,
                typ: Number(values.typ),
                on_time: values.on_time ? Math.floor(values.on_time.getTime() / 1000) : null,
                off_time: values.off_time ? Math.floor(values.off_time.getTime() / 1000) : null
            });
            if (response.errno === 0) {
                Toast.success('添加成功');
                setShowAddModal(false);
                fetchList();
            } else {
                Toast.error(response.errstr || '添加失败');
            }
        } catch (error) {
            console.error('添加失败:', error);
            Toast.error('添加失败');
        }
    };

    const handleEdit = async (values) => {
        if (!values.image) {
            Toast.error('请选择图片');
            return;
        }
        try {
            const response = await request.post('/go/sp/all/edit_all_slide/', {
                ...values,
                typ: Number(values.typ),
                uid: currentRecord.uid,
                on_time: values.on_time ? Math.floor(values.on_time.getTime() / 1000) : null,
                off_time: values.off_time ? Math.floor(values.off_time.getTime() / 1000) : null
            });
            if (response.errno === 0) {
                Toast.success('编辑成功');
                setShowEditModal(false);
                fetchList();
            } else {
                Toast.error(response.errstr || '编辑失败');
            }
        } catch (error) {
            console.error('编辑失败:', error);
            Toast.error('编辑失败');
        }
    };

    const handleDelete = async (uids) => {
        try {
            const response = await request.post('/go/sp/all/delete_all_slide/', {
                uids: Array.isArray(uids) ? uids.join(',') : uids
            });
            if (response.errno === 0) {
                Toast.success('删除成功');
                setSelectedRowKeys([]);
                fetchList();
            } else {
                Toast.error(response.errstr || '删除失败');
            }
        } catch (error) {
            console.error('删除失败:', error);
            Toast.error('删除失败');
        }
    };

    const handleBatchEdit = async (status, uids) => {
        if (!uids || (Array.isArray(uids) && !uids.length)) {
            Toast.warning('请选择要操作的轮播图');
            return;
        }
        try {
            const response = await request.post('/go/sp/all/batchedit_all_slide/', {
                uids: Array.isArray(uids) ? uids.join(',') : uids,
                status
            });
            if (response.errno === 0) {
                Toast.success('操作成功');
                setSelectedRowKeys([]);
                fetchList();
            } else {
                Toast.error(response.errstr || '操作失败');
            }
        } catch (error) {
            console.error('操作失败:', error);
            Toast.error('操作失败');
        }
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '-';
        const date = new Date(parseInt(timestamp));
        return date.toLocaleString();
    };

    const columns = [
        {
            title: '轮播图',
            dataIndex: 'image',
            width: 250,
            render: (text) => (
                <UniImage
                    src={text}
                    alt="轮播图"
                    height={100}
                    style={{ objectFit: 'contain' }}
                />
            )
        },
        {
            title: '分类类型',
            dataIndex: 'typ',
            width: 150,
            render: (value) => {
                const type = typList.find(t => t.uid == value);
                return type ? type.name : '-';
            }
        },
        {
            title: '信息',
            width: 250,
            render: (_, record) => (
                <div style={{ fontSize: '12px' }}>
                    <div style={{
                        color: 'var(--semi-color-text-0)',
                        marginBottom: 4,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                    }}>
                        {record.title}
                    </div>
                    <a
                        target="_blank"
                        rel="noreferrer"
                        style={{
                            color: 'var(--semi-color-link)',
                            fontSize: '12px',
                            display: 'block',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }}
                    >
                        {record.link}
                    </a>
                </div>
            )
        },
        {
            title: '状态',
            dataIndex: 'status',
            width: 100,
            render: (text) => {
                const status = statusMap[text] || { text: '异常', type: 'danger' };
                return <Button theme='light' type={status.type} size='small'>{status.text}</Button>;
            }
        },
        {
            title: '排序',
            dataIndex: 'sort',
            width: 80
        },
        {
            title: '创建时间',
            dataIndex: 'create_time',
            width: 180,
            render: formatTimestamp
        },
        {
            title: '生效时间',
            width: 200,
            render: (_, record) => (
                <div style={{ fontSize: '12px', color: 'var(--semi-color-text-2)' }}>
                    <div>上线: {formatTimestamp(record.on_time * 1000)}</div>
                    <div>下线: {formatTimestamp(record.off_time * 1000)}</div>
                </div>
            )
        },
        {
            title: '操作',
            dataIndex: 'action',
            width: 150,
            render: (text, record) => (
                <Space gap="medium">
                    <Button
                        icon={<IconEdit />}
                        onClick={() => {
                            setCurrentRecord(record);
                            setShowEditModal(true);
                        }}
                    >
                        编辑
                    </Button>
                    <Popconfirm
                        title="确定删除吗？"
                        onConfirm={() => handleDelete(record.uid)}
                    >
                        <Button icon={<IconDelete />} type="danger">删除</Button>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <Space>
                    <Input
                        placeholder="搜索标题"
                        value={searchKey}
                        onChange={setSearchKey}
                        style={{ width: 200 }}
                        showClear
                    />
                    <Select
                        placeholder="选择状态"
                        value={searchStatus}
                        onChange={setSearchStatus}
                        style={{ width: 150 }}
                        showClear
                    >
                        <Select.Option value={null}>全部状态</Select.Option>
                        {Object.entries(statusMap).map(([value, { text }]) => (
                            <Select.Option key={value} value={parseInt(value)}>{text}</Select.Option>
                        ))}
                    </Select>
                    {!urlTyp && (
                        <Select
                            placeholder="选择类型"
                            value={searchTyp}
                            onChange={setSearchTyp}
                            style={{ width: 150 }}
                            showClear
                        >
                            <Select.Option value={null}>全部类型</Select.Option>
                            {typList.map(typ => (
                                <Select.Option key={typ.uid} value={typ.uid}>{typ.name}</Select.Option>
                            ))}
                        </Select>
                    )}
                    <Button
                        type="primary"
                        onClick={() => fetchList(1, pagination.pageSize)}
                        loading={loading}
                        icon={<IconSearch />}
                    >
                        搜索
                    </Button>
                    <Button
                        type="primary"
                        icon={<IconPlus />}
                        onClick={() => setShowAddModal(true)}
                    >
                        新增
                    </Button>
                    {selectedRowKeys.length > 0 && (
                        <>
                            <Button
                                type="warning"
                                onClick={() => handleBatchEdit(1, selectedRowKeys)}
                                icon={<IconStop />}
                            >
                                批量隐藏
                            </Button>
                            <Button
                                type="success"
                                onClick={() => handleBatchEdit(0, selectedRowKeys)}
                                icon={<IconPlayCircle />}
                            >
                                批量显示
                            </Button>
                            <Popconfirm
                                title="确定要删除选中的轮播图吗？"
                                onConfirm={() => handleDelete(selectedRowKeys)}
                            >
                                <Button type="danger" icon={<IconDelete />}>
                                    批量删除
                                </Button>
                            </Popconfirm>
                        </>
                    )}
                </Space>
            </div>

            <Table
                columns={columns}
                dataSource={dataSource}
                loading={loading}
                rowKey="uid"
                pagination={{
                    currentPage: pagination.currentPage,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    onChange: (page, pageSize) => {
                        setPagination(prev => ({ ...prev, currentPage: page, pageSize }));
                        fetchList(page, pageSize);
                    }
                }}
                rowSelection={{
                    selectedRowKeys,
                    onChange: setSelectedRowKeys
                }}
            />

            <Modal
                title={showAddModal ? '新增轮播图' : '编辑轮播图'}
                visible={showAddModal || showEditModal}
                onCancel={() => {
                    setShowAddModal(false);
                    setShowEditModal(false);
                }}
                footer={null}
            >
                <Form
                    initValues={{
                        ...currentRecord,
                        typ: currentRecord.typ
                    }}
                    getFormApi={api => setFormApi(api)}
                    onSubmit={values => showAddModal ? handleAdd(values) : handleEdit(values)}
                >
                    <Form.Input
                        field="title"
                        label="标题"
                    />
                    <Form.Select
                        field="typ"
                        label="轮播类型"
                        placeholder="请选择类型"
                        rules={[{ required: true, message: '请选择轮播类型' }]}
                        filter
                        showClear
                        disabled={!!urlTyp}
                        initValue={urlTyp ? Number(urlTyp) : undefined}
                    >
                        {typList.map(typ => (
                            <Select.Option key={typ.uid} value={Number(typ.uid)}>{typ.name}</Select.Option>
                        ))}
                    </Form.Select>
                    <Form.Slot label=" ">
                        <ImageSelector
                            value={currentRecord.image}
                            onChange={value => formApi?.setValue('image', value)}
                            enableTDesignIcon={true}
                            width="100%"
                        />
                    </Form.Slot>
                    <div style={{ color: 'var(--semi-color-text-2)', fontSize: 12, marginTop: 8 }}>推荐尺寸：900px × 383px</div>
                    <Form.Slot label="链接地址">
                        <LinkSelector
                            value={formApi?.getValue('link')}
                            onChange={value => formApi?.setValue('link', value)}
                            showParams={true}
                        />
                    </Form.Slot>
                    <Form.InputNumber
                        field="sort"
                        label="排序"
                    />
                    <Form.Select
                        field="status"
                        label="状态"
                        initValue={0}
                    >
                        {Object.entries(statusMap).map(([value, { text }]) => (
                            <Select.Option key={value} value={parseInt(value)}>{text}</Select.Option>
                        ))}
                    </Form.Select>
                    <Form.DatePicker
                        field="on_time"
                        label="上线时间"
                        type="dateTime"
                        initValue={currentRecord.on_time ? new Date(parseInt(currentRecord.on_time) * 1000) : null}
                    />
                    <Form.DatePicker
                        field="off_time"
                        label="下线时间"
                        type="dateTime"
                        initValue={currentRecord.off_time ? new Date(parseInt(currentRecord.off_time) * 1000) : null}
                    />
                    <Button type="primary" htmlType="submit">
                        保存
                    </Button>
                </Form>
            </Modal>
        </div >
    );
};

export default Slides;